import * as React from "react"
import { Clock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { ScrollArea } from "@/components/ui/scroll-area"

interface TimePickerDemoProps {
  value?: string;
  onChange: (time: string) => void;
}

// Quick preset times
const QUICK_TIMES = [
  { label: "בוקר", time: "08:00" },
  { label: "צהריים", time: "12:00" },
  { label: "אחה״צ", time: "16:00" },
  { label: "ערב", time: "20:00" },
];

// Generate hours array (6-22)
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6);

// Generate minutes array (00, 15, 30, 45)
const MINUTES = [0, 15, 30, 45];

export function TimePickerDemo({ value, onChange }: TimePickerDemoProps) {
  const [open, setOpen] = React.useState(false)
  const [manualTime, setManualTime] = React.useState(value || "")
  const [selectedHour, setSelectedHour] = React.useState<number | null>(
    value ? parseInt(value.split(":")[0], 10) : null
  )

  React.useEffect(() => {
    if (value) {
      setSelectedHour(parseInt(value.split(":")[0], 10))
      setManualTime(value)
    }
  }, [value])

  const handleQuickSelect = (time: string) => {
    onChange(time)
    setManualTime(time)
    setOpen(false)
  }

  const handleTimeSelect = (hour: number, minute: number) => {
    const timeString = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`
    onChange(timeString)
    setManualTime(timeString)
    setOpen(false)
  }

  const handleHourClick = (hour: number) => {
    setSelectedHour(hour)
  }

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/[^\d:]/g, "")
    
    // Auto-add colon after 2 digits
    if (input.length === 2 && !input.includes(":")) {
      input = input + ":"
    }
    
    // Limit to HH:MM format
    if (input.length > 5) {
      input = input.slice(0, 5)
    }
    
    setManualTime(input)
  }

  const handleManualSubmit = () => {
    const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/
    if (timeRegex.test(manualTime)) {
      const [h, m] = manualTime.split(":")
      const formattedTime = `${h.padStart(2, "0")}:${m}`
      onChange(formattedTime)
      setManualTime(formattedTime)
      setSelectedHour(parseInt(h, 10))
      setOpen(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleManualSubmit()
    }
  }

  const isValidTime = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/.test(manualTime)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal bg-secondary text-secondary-foreground border-input hover:bg-secondary/80",
            !value && "text-muted-foreground"
          )}
        >
          <Clock className="mr-2 h-4 w-4" />
          {value ? value : "בחר שעה"}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[320px] p-0 bg-popover border-border" 
        align="start"
      >
        {/* Manual input */}
        <div className="p-3 border-b border-border">
          <p className="text-xs text-muted-foreground mb-2 text-right">הקלד שעה</p>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="HH:MM"
              value={manualTime}
              onChange={handleManualInput}
              onKeyDown={handleKeyDown}
              className="text-center font-mono text-lg bg-secondary border-input"
              dir="ltr"
            />
            <Button
              type="button"
              size="sm"
              disabled={!isValidTime}
              onClick={handleManualSubmit}
              className="px-4"
            >
              אישור
            </Button>
          </div>
        </div>

        {/* Quick presets */}
        <div className="p-3 border-b border-border">
          <p className="text-xs text-muted-foreground mb-2 text-right">בחירה מהירה</p>
          <div className="grid grid-cols-4 gap-2">
            {QUICK_TIMES.map((preset) => (
              <Button
                key={preset.time}
                type="button"
                variant={value === preset.time ? "default" : "secondary"}
                size="sm"
                className="text-xs h-9"
                onClick={() => handleQuickSelect(preset.time)}
              >
                <div className="flex flex-col items-center">
                  <span className="font-medium">{preset.label}</span>
                  <span className="text-[10px] opacity-70">{preset.time}</span>
                </div>
              </Button>
            ))}
          </div>
        </div>

        {/* Hour selection */}
        <div className="p-3 border-b border-border">
          <p className="text-xs text-muted-foreground mb-2 text-right">שעה</p>
          <ScrollArea className="h-[100px]">
            <div className="grid grid-cols-6 gap-1.5">
              {HOURS.map((hour) => (
                <Button
                  key={hour}
                  type="button"
                  variant={selectedHour === hour ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-8 text-sm font-medium",
                    selectedHour === hour && "ring-2 ring-primary ring-offset-1 ring-offset-background"
                  )}
                  onClick={() => handleHourClick(hour)}
                >
                  {hour.toString().padStart(2, "0")}
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Minute selection - only show when hour is selected */}
        {selectedHour !== null && (
          <div className="p-3">
            <p className="text-xs text-muted-foreground mb-2 text-right">דקות</p>
            <div className="grid grid-cols-4 gap-2">
              {MINUTES.map((minute) => {
                const timeString = `${selectedHour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`
                const isSelected = value === timeString
                return (
                  <Button
                    key={minute}
                    type="button"
                    variant={isSelected ? "default" : "secondary"}
                    size="sm"
                    className="h-10 text-sm font-medium"
                    onClick={() => handleTimeSelect(selectedHour, minute)}
                  >
                    :{minute.toString().padStart(2, "0")}
                  </Button>
                )
              })}
            </div>
          </div>
        )}

        {/* Hint when no hour selected */}
        {selectedHour === null && (
          <div className="p-3 text-center text-sm text-muted-foreground">
            בחר שעה או הקלד ידנית
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
